import React from 'react';
import { Radar } from 'react-chartjs-2';
import './ChartRegistry'; // Import to ensure chart components are registered
import { baseChartOptions } from './ChartRegistry';

interface RadarChartProps {
  labels: string[];
  data: number[];
  title: string;
}

/**
 * RadarChart Component
 * 
 * Renders a radar chart visualization using Chart.js and react-chartjs-2.
 * This component displays data points across multiple variables on a radar/spider chart.
 * 
 * Props:
 * - labels: Array of strings representing the categories/dimensions displayed around the radar
 * - data: Array of numbers (0-100) representing the values for each category
 * - title: String displayed as both the chart legend and header
 * 
 * The chart is configured with:
 * - A scale from 0-100 with 20-unit steps
 * - Light blue fill with darker blue border
 * - Responsive sizing within a fixed height container
 * - White background with rounded corners and shadow for visual appeal
 */
export const RadarChart: React.FC<RadarChartProps> = ({ labels, data, title }) => {
  const chartData = {
    labels,
    datasets: [
      {
        label: title,
        data,
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    ...baseChartOptions,
    scales: {
      r: {
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20,
        },
      },
    },
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-md h-full">
      <h3 className="text-lg font-semibold mb-3">{title}</h3>
      <div className="h-[400px]">
        <Radar data={chartData} options={options} />
      </div>
    </div>
  );
};
